import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, ArrowRight, Check, Plus, RotateCcw, X } from '../../icons';
import {
  Button, Checkbox, DatePicker, EmptyState, FormField, Input, PageHeader, Select, Table,
  type TableColumn,
} from '../../components/shared';
// 쪽 머리 경로 · 범위 칩은 메뉴 배치에서(2026-10-17 · web 레인 W6 — lib/page-crumbs.ts)
import { crumbsFor, scopeFor } from '../../lib/page-crumbs';
import {
  listCompanyHolidays, saveCompanyHoliday, deleteCompanyHoliday,
  saveHolidaySubstitution, deleteHolidaySubstitution,
  HOLIDAY_KIND_LABEL,
  type SystemHolidayDto, type CompanyHolidayDto, type EffectiveHolidayDto,
} from '../../api/hr-holiday';
import './AttendanceAdmin.css';

/**
 * 화면 K — **회사 일정**(공휴일 두 층). 근태 4단계, 2026-08-24.
 *
 * 설계: `docs/attendance-round2-sample.html` 제안 5.
 *
 *   "휴일은 회사가 실제로 쉬는 날이지 나라가 정한 목록이 아니다. 관공서 공휴일을 다 쉬지 않는
 *    회사도 있고, 나라에 없는 날을 쉬는 회사도 있다."
 *
 * ★★ 이 화면은 **전역 목록을 지우지 않는다.** 시스템 일정 옆의 "안 쉼" 은 회사 줄(`EXCLUDE`)을
 *    하나 쌓는 것이다 — 지금 관리 콘솔의 삭제는 전역 표를 건드려서 **한 회사가 지우면 모든
 *    회사에서 사라진다**. 여기서는 2순위가 다치지 않는다.
 * ★ 합친 결과를 함께 보여 준다 — 근태·급여·캘린더가 보는 것이 바로 이 목록이다.
 *
 * ── 정본 채택 (2026-09-06 · 채택 3단계 ③ 근태 · 기준 셋) ─────────────────────────
 * `PageHeader` · `Select` · `DatePicker` · `FormField` · `Input` · `Checkbox` · `Table` ×2 ·
 * `Button` · `EmptyState`.
 *
 * ★★★ **「두 층」과 「서면 합의」는 화면에 남겼다** — 정본 P8 의 예외다.
 *    앞은 **왜 이 단추가 «지우기»가 아니라 «안 쉼»인가**를 설명한다(모르면 전역 목록을 지우는
 *    줄 알고 손이 멎는다). 뒤는 **법이 요구하는 것**이라 안 보이면 근거 없이 옮기게 된다.
 *    둘 다 이 화면이 세워질 때 검사로 못 박은 계약이기도 하다. 자세한 설명만 도움말로 옮겼다.
 * ★★ **날짜 칸은 정본 `DatePicker` 로 옮겼다** — 여기 날짜는 **그 해 안의 가까운 날**이라
 *    달 단위 이동으로 충분하다(입사일처럼 먼 과거를 걷지 않는다 · 정본 C1 예외의 반대 경우).
 */

const ERRORS: Record<string, string> = {
  INVALID_DATE: '없는 날짜입니다.',
  FORBIDDEN: '이 화면은 회사 관리자만 쓸 수 있습니다.',
};

export function CompanyHolidayPage(): JSX.Element {
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [system, setSystem] = useState<SystemHolidayDto[]>([]);
  const [company, setCompany] = useState<CompanyHolidayDto[]>([]);
  const [effective, setEffective] = useState<EffectiveHolidayDto[]>([]);
  const [draft, setDraft] = useState({ date: '', name: '', isPaid: true });
  /**
   * 휴일대체 (2026-08-30 · 판 K) — 「그 공휴일은 안 쉬고 이 날 쉰다」.
   * ★ 두 줄(EXCLUDE·ADD)을 손으로 만들게 하면 **하나만 만들고 마는 일**이 반드시 생긴다.
   */
  const [swap, setSwap] = useState({ fromDate: '', toDate: '', note: '' });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listCompanyHolidays(year);
      setSystem(data.system);
      setCompany(data.company);
      setEffective(data.effective);
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code ?? '';
      setError(ERRORS[code] ?? '회사 일정을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { void load(); }, [load]);

  const excludedOf = (date: string) => company.find((c) => c.date === date && c.kind === 'EXCLUDE');

  const toggleExclude = async (h: SystemHolidayDto) => {
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      const row = excludedOf(h.date);
      if (row) {
        await deleteCompanyHoliday(row.id);
        setDone(`${h.name} 을 다시 쉽니다.`);
      } else {
        await saveCompanyHoliday({ date: h.date, name: h.name, kind: 'EXCLUDE', sourceHolidayId: h.id });
        setDone(`${h.name} 은 쉬지 않는 날로 두었습니다 — 전역 목록은 그대로입니다.`);
      }
      await load();
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code ?? '';
      setError(ERRORS[code] ?? '바꾸지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const addCompanyDay = async () => {
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      await saveCompanyHoliday({ date: draft.date, name: draft.name.trim(), kind: 'ADD', isPaid: draft.isPaid });
      setDraft({ date: '', name: '', isPaid: true });
      setDone('회사 휴일을 더했습니다.');
      await load();
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code ?? '';
      setError(ERRORS[code] ?? '저장하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  /** 그 해 대체 목록 — `ADD` 쪽 줄이 «어느 날 대신»을 이고 있다. */
  const swaps = useMemo(
    () => company.filter((c) => c.kind === 'ADD' && c.substituteForDate),
    [company],
  );

  /** 옮길 수 있는 날 — **지금 쉬는 날**만 고를 수 있어야 한다(평일을 옮긴다는 말은 성립 안 한다). */
  const swappable = useMemo(
    () => effective.filter((h) => !swaps.some((s) => s.substituteForDate === h.date)),
    [effective, swaps],
  );

  const saveSwap = async () => {
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      const res = await saveHolidaySubstitution({
        fromDate: swap.fromDate, toDate: swap.toDate, note: swap.note.trim() || undefined,
      });
      setSwap({ fromDate: '', toDate: '', note: '' });
      setDone(`${res.name} — ${res.fromDate} 대신 ${res.toDate} 에 쉽니다.`);
      await load();
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code ?? '';
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      // ★ 서버가 «왜 안 되는지»를 사람 말로 준다 — 그대로 보여 준다(다시 쓰면 둘이 갈라진다).
      setError(message ?? ERRORS[code] ?? '옮기지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const undoSwap = async (row: CompanyHolidayDto) => {
    if (!row.substituteForDate) return;
    setBusy(true);
    setError(null);
    try {
      await deleteHolidaySubstitution(row.substituteForDate, row.date);
      setDone('대체를 되돌렸습니다 — 원래 날에 다시 쉽니다.');
      await load();
    } catch {
      setError('되돌리지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const removeCompanyDay = async (row: CompanyHolidayDto) => {
    setBusy(true);
    setError(null);
    try {
      await deleteCompanyHoliday(row.id);
      setDone('회사 줄을 지웠습니다 — 시스템 목록은 그대로입니다.');
      await load();
    } catch {
      setError('지우지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const systemCols: ReadonlyArray<TableColumn<SystemHolidayDto>> = useMemo(() => [
    { key: 'date', header: '날짜', width: '124px', rowHeader: true, render: (h) => h.date },
    {
      key: 'name',
      header: '이름',
      render: (h) => <span className={excludedOf(h.date) ? 'is-muted' : undefined}>{h.name}</span>,
    },
    { key: 'note', header: '비고', width: '110px', render: (h) => <span className="is-muted">{h.isSubstitute ? '대체공휴일' : ''}</span> },
    {
      key: 'ours',
      header: '우리 회사',
      width: '112px',
      render: (h) => {
        const excluded = Boolean(excludedOf(h.date));
        return (
          <Button
            variant={excluded ? 'secondary' : 'primary'}
            size="sm"
            onClick={() => { void toggleExclude(h); }}
            disabled={busy}
          >
            {excluded ? '다시 쉬기' : '쉼'}
          </Button>
        );
      },
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [company, busy]);

  const effectiveCols: ReadonlyArray<TableColumn<EffectiveHolidayDto>> = useMemo(() => [
    { key: 'date', header: '날짜', width: '124px', rowHeader: true, render: (h) => h.date },
    { key: 'name', header: '이름', render: (h) => h.name },
    {
      key: 'paid',
      header: '유급',
      width: '80px',
      render: (h) => <span className={h.isPaid ? undefined : 'is-warn'}>{h.isPaid ? '유급' : '무급'}</span>,
    },
    {
      key: 'source',
      header: '출처',
      width: '150px',
      render: (h) => (
        <span className={h.source === 'COMPANY' ? 'is-strong' : 'is-muted'}>
          {h.source === 'COMPANY' ? `회사${h.kind ? ` · ${HOLIDAY_KIND_LABEL[h.kind]}` : ''}` : '시스템'}
        </span>
      ),
    },
  ], []);

  return (
    <div className="hra">
      <PageHeader
        breadcrumb={crumbsFor('/erp/attendance/holidays')}
        scope={scopeFor('/erp/attendance/holidays')}
        title="회사 일정"
        description={`${year}년 · 표시와 계산의 기준은 회사 일정입니다.`}
        help={{
          title: '이 화면의 규칙',
          body: (
            <>
              <p>휴일은 <b>회사가 실제로 쉬는 날</b>이지 나라가 정한 목록이 아닙니다. 관공서
                공휴일을 다 쉬지 않는 회사도 있고, 나라에 없는 날을 쉬는 회사도 있습니다.</p>
              <p><b>2순위 시스템 일정</b>은 회사가 아직 정하지 않은 날을 채우는 바탕이고 <b>읽기만</b>
                합니다. 그래서 「안 쉼」은 그 목록을 지우지 않고 <b>회사 줄을 하나 쌓습니다</b> —
                다른 회사의 목록은 다치지 않습니다.</p>
              <p><b>휴일 대체</b>는 두 줄(빼기 · 더하기)을 한 번에 만듭니다. 손으로 두 줄을 만들게
                하면 하나만 만들고 마는 일이 생기고, 그러면 휴일이 사라지거나 하루가 더 늡니다.</p>
              <p>공휴일을 다른 날로 바꾸려면 <b>근로자대표와의 서면 합의</b>가 필요합니다(근기법
                §55②). 시스템은 합의서를 확인할 수 없어 <b>막지 않고 근거만 남깁니다</b>.</p>
            </>
          ),
        }}
        actions={(
          <Select
            ariaLabel="연도 선택"
            size="sm"
            value={String(year)}
            onChange={(v) => setYear(Number(v))}
            options={[year + 1, year, year - 1].map((y) => ({ value: String(y), label: `${y}년` }))}
          />
        )}
      />

      {/*
        ★★★ 이 칸은 도움말로 안 보낸다 — **왜 이 단추가 «지우기»가 아니라 «안 쉼»인가**를
        말하는 자리다. 모르면 전역 목록을 지우는 줄 알고 손이 멎는다(정본 P8 의 예외).
      */}
      <section className="hra__rule" aria-label="두 층">
        <h2 className="hra__rulehead">공휴일은 두 층입니다</h2>
        <ul className="hra__rulelist">
          <li><b>1순위 회사 일정</b> — 여기서 정한 것이 표시와 계산의 기준입니다.</li>
          <li><b>2순위 시스템 일정</b> — 회사가 아직 정하지 않은 날을 채우는 바탕입니다(읽기만).</li>
          <li>“안 쉼”은 <b>지우는 것이 아니라 한 줄을 쌓는 것</b>입니다 — 다른 회사의 목록은 다치지 않습니다.</li>
        </ul>
      </section>

      {error && <p className="hra__error" role="alert"><AlertCircle size={14} aria-hidden />{error}</p>}
      {done && !error && <p className="hra__ok" role="status"><Check size={14} aria-hidden />{done}</p>}

      <div className="hra__split">
        <aside className="hra__aside" aria-label="회사 휴일 더하기">
          <h2 className="hra__rulehead">회사 휴일 더하기 <span className="hra__dim">창립기념일 등</span></h2>
          <div className="hra__form">
            <FormField label="날짜">
              <DatePicker value={draft.date || null} onChange={(v) => setDraft({ ...draft, date: v ?? '' })} />
            </FormField>
            <FormField label="이름">
              <Input maxLength={100} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </FormField>
            <Checkbox
              label="유급"
              checked={draft.isPaid}
              onChange={(e) => setDraft({ ...draft, isPaid: e.target.checked })}
            />
          </div>
          <div className="hra__actions">
            <Button
              iconLeft={<Plus size={14} aria-hidden />}
              onClick={() => { void addCompanyDay(); }}
              disabled={busy || !draft.date || !draft.name.trim()}
            >
              더하기
            </Button>
          </div>

          <h3 className="hra__rulehead">회사가 정한 날 {company.length}건</h3>
          <ul className="hra__versions">
            {company.length === 0 && <li className="hra__dim">아직 없습니다 — 관공서 공휴일을 전부 쉽니다.</li>}
            {company.map((c) => (
              <li key={c.id}>
                <span className="hra__linkbtn">
                  <b>{c.date} · {c.name}</b>
                  <span>{HOLIDAY_KIND_LABEL[c.kind]}{c.kind !== 'EXCLUDE' ? (c.isPaid ? ' · 유급' : ' · 무급') : ''}</span>
                </span>
                <Button
                  variant="quiet-danger" size="sm" iconOnly
                  onClick={() => { void removeCompanyDay(c); }}
                  disabled={busy}
                  aria-label={`${c.name} 회사 줄 지우기`}
                >
                  <X size={13} aria-hidden />
                </Button>
              </li>
            ))}
          </ul>

          {/*
            ★★ 휴일대체 — 「그 공휴일은 안 쉬고 이 날 쉰다」. 두 줄(EXCLUDE·ADD)을 손으로
               만들게 하면 **하나만 만들고 마는 일**이 반드시 생긴다. 그래서 **한 번에** 만든다.
          */}
          <h3 className="hra__rulehead">휴일 대체 <span className="hra__dim">쉬는 날을 옮긴다</span></h3>
          <div className="hra__form">
            <FormField label="원래 쉬던 날">
              <Select
                ariaLabel="원래 쉬던 날"
                placeholder="고르기"
                value={swap.fromDate || null}
                onChange={(v) => setSwap({ ...swap, fromDate: v })}
                options={swappable.map((h) => ({ value: h.date, label: `${h.date} · ${h.name}` }))}
              />
            </FormField>
            <FormField label="대신 쉬는 날">
              <DatePicker
                ariaLabel="대신 쉬는 날"
                value={swap.toDate || null}
                onChange={(v) => setSwap({ ...swap, toDate: v ?? '' })}
              />
            </FormField>
            <FormField label="근거" hint="무엇에 근거했는지 적어 두세요.">
              <Input
                maxLength={200} aria-label="대체 근거"
                placeholder="근로자대표 합의 2026-07-01"
                value={swap.note}
                onChange={(e) => setSwap({ ...swap, note: e.target.value })}
              />
            </FormField>
          </div>
          {/* ★★ 법이 요구하는 것이라 화면에 남는다 — 안 보이면 근거 없이 옮기게 된다(P8 예외). */}
          <p className="hra__note">
            공휴일을 다른 날로 바꾸려면 <b>근로자대표와의 서면 합의</b>가 필요합니다(근기법 §55②).
            시스템은 합의서를 확인할 수 없으니 <b>막지 않고 근거만 남깁니다</b>.
          </p>
          <div className="hra__actions">
            <Button
              iconLeft={<ArrowRight size={14} aria-hidden />}
              onClick={() => { void saveSwap(); }}
              disabled={busy || !swap.fromDate || !swap.toDate}
            >
              옮기기
            </Button>
          </div>

          <h3 className="hra__rulehead">옮긴 날 {swaps.length}건</h3>
          <ul className="hra__versions">
            {swaps.length === 0 && <li className="hra__dim">옮긴 날이 없습니다.</li>}
            {swaps.map((c) => (
              <li key={c.id}>
                <span className="hra__linkbtn">
                  <b>{c.substituteForDate} → {c.date}</b>
                  <span>{c.name}{c.substituteNote ? ` · ${c.substituteNote}` : ''}</span>
                </span>
                <Button
                  variant="quiet-danger" size="sm" iconOnly disabled={busy}
                  aria-label={`${c.name} 대체 되돌리기`}
                  onClick={() => { void undoSwap(c); }}
                >
                  <RotateCcw size={13} aria-hidden />
                </Button>
              </li>
            ))}
          </ul>
        </aside>

        <section className="hra__main">
          <Table
            caption={`시스템 일정 ${system.length}건 — 쉬지 않기로 한 날은 「안 쉼」으로 표시됩니다(전역 목록은 그대로)`}
            className="hra-tbl"
            columns={systemCols}
            rows={system}
            rowKey={(h) => h.id}
            loading={loading}
            rowClassName={(h) => (excludedOf(h.date) ? 'is-muted' : undefined)}
            empty={<EmptyState title="그 해 시스템 일정이 없습니다." description="다른 연도를 골라 보세요." />}
          />

          <p className="hra__note">
            아래는 <b>합친 결과 {effective.length}일</b> — 근태 · 급여 · 캘린더가 보는 것이 이 목록입니다.
          </p>
          <Table
            caption="합친 결과"
            className="hra-tbl"
            columns={effectiveCols}
            rows={effective}
            rowKey={(h) => h.date}
            empty={<EmptyState title="쉬는 날이 없습니다." description="시스템 일정도 회사 일정도 없습니다." />}
          />
        </section>
      </div>
    </div>
  );
}
